import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowUpRight, Instagram, Youtube } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("You are on the list. Welcome.");
      setEmail("");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[color:var(--sr-primary)] text-[color:var(--sr-secondary)] pt-24 md:pt-32 pb-10 grain"
    >
      <div className="sr-container relative z-10 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.9 }}
          className="md:col-span-6"
        >
          <p className="label-eyebrow opacity-70">Newsletter</p>
          <h3 className="font-serif text-4xl md:text-6xl leading-[0.95] tracking-tight mt-3 mb-8 max-w-xl">
            Slow letters, seasonal drops, occasional recipes.
          </h3>
          <form
            onSubmit={subscribe}
            className="flex items-end gap-4 max-w-md border-b border-[color:var(--sr-secondary)]/40 pb-2"
            data-testid="newsletter-form"
          >
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              data-testid="newsletter-email"
              className="flex-1 bg-transparent border-0 outline-none text-[color:var(--sr-secondary)] placeholder:text-[color:var(--sr-secondary)]/50 py-2 text-base"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="newsletter-submit"
              className="label-eyebrow flex items-center gap-2 hover:opacity-70 transition-opacity disabled:opacity-40 min-h-[44px] px-3"
            >
              {loading ? "Sending" : "Subscribe"}
              <ArrowUpRight size={16} strokeWidth={1.25} />
            </button>
          </form>
        </motion.div>

        <div className="md:col-span-6 grid grid-cols-2 sm:grid-cols-3 gap-10">
          <FooterCol
            title="Shop"
            links={[
              { to: "/shop", label: "All beverages" },
              { to: "/product/golden-nectar", label: "Golden Nectar" },
              { to: "/product/dark-fudge", label: "Dark Fudge" },
              { to: "/product/hibiscus-bloom", label: "Hibiscus Bloom" },
            ]}
          />
          <FooterCol
            title="House"
            links={[
              { to: "/about", label: "Our Story" },
              { to: "/markets", label: "Farmers Markets" },
              { to: "/contact", label: "Contact" },
            ]}
          />
          <FooterCol
            title="Elsewhere"
            links={[
              { href: "#", label: "Instagram", icon: Instagram },
              { href: "#", label: "YouTube", icon: Youtube },
            ]}
          />
        </div>
      </div>

      <div className="sr-container mt-24 md:mt-32 flex flex-col md:flex-row items-start md:items-end justify-between gap-6 border-t border-[color:var(--sr-secondary)]/25 pt-8 relative z-10">
        <p className="font-serif text-[color:var(--sr-secondary)]/70 text-sm">
          © {new Date().getFullYear()} The Smiling Roots — Rooted in nature. Crafted for wellness.
        </p>
        <p className="label-eyebrow opacity-70">
          Small batch · California, USA
        </p>
      </div>

      {/* Oversize brand emblem — subtle watermark */}
      <div className="pointer-events-none select-none absolute -bottom-8 md:-bottom-16 left-1/2 -translate-x-1/2 opacity-[0.12] mix-blend-screen">
        <img
          src="/logo.png"
          alt=""
          aria-hidden="true"
          className="w-[80vw] max-w-[900px] h-auto"
        />
      </div>
    </footer>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <p className="label-eyebrow opacity-70 mb-4">{title}</p>
      <ul className="space-y-3">
        {links.map((l) => (
          <li key={l.label}>
            {l.to ? (
              <Link
                to={l.to}
                className="font-serif text-xl leading-tight hover:text-[color:var(--sr-accent-light)] transition-colors"
              >
                {l.label}
              </Link>
            ) : (
              <a
                href={l.href}
                className="font-serif text-xl leading-tight inline-flex items-center gap-2 hover:text-[color:var(--sr-accent-light)] transition-colors"
              >
                {l.icon ? <l.icon size={16} strokeWidth={1.25} /> : null}
                {l.label}
              </a>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
